import Section from "../../base/Section";
import PageBreadcrumb, {
  PageBreadcrumbItem,
} from "../../common/PageBreadcrumb";
import StoreCatalogSearch from "./StoreCatalogSearch";
import { Col, Row } from "react-bootstrap";
import VerifiedBadge from "../../base/Tags/VerifiedBadge";
import { useParams, useNavigate } from "react-router-dom";
import CategoryTag from "../../base/Tags/CategoryTag";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { Tab, Nav } from "react-bootstrap";
import EcomTienda from "../../tables/EcomTienda";
import EcomProfileReviewsTable from "../../tables/EcomStoreReviewsTable";
import EcomPhotosVideo from "../../tables/EcomPhotosTable";
import { faHome, faStar, faHeart } from "@fortawesome/free-solid-svg-icons";
import ScrollSpy from "../../common/ScrollSpy";
import { getImage } from "../../../api/images/images";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  getProviderCategories,
  getLocations,
  isVerifiedUser,
} from "../../../helpers/utils";
import { useProviderStore } from "../../../store/providerStore";
import LocationTag from "../../base/Tags/LocationTag";
import { getProductsofProvider, getProviders } from "../../../api";
import ShareProfileModal from "../../common/ShareProfileModal";
import useModal from "../../../hooks/useModal";
import "./Store.css";

// so a user clicks on ProductCard and it will redirect them to the store of the page they clicked on. In the ProcuctCard component, when a user clicks on the card, we will use the products ID in the url so that the store page can fetch the data based on the ID
// on this component, we will retrieve the product id from the URL params, then use this id to load the correct data for the store. each store has a different id.

const Store = () => {
  // Extract the storeName from the URL parameters
  const { storeName } = useParams<{ storeName: string }>();
  const { provider, setProvider } = useProviderStore();
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const navigate = useNavigate();
  // will be used for catalog search
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<string>("tienda"); // Default active tab, we will use this state to keep track of the active tab and pass it to the StoreTabs component
  const { showModal, handleShowModal, handleCloseModal } = useModal();

  const fetchImageSafe = async (imageId: string) => {
    try {
      return await getImage(imageId);
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const fetchProviderData = async (storeName: string) => {
    window.scrollTo(0, 0);
    const { data } = await getProviders({ params: { uniqueNames: storeName } });
    const [providerData] = data;
    setProvider(providerData);

    try {
      const providerProducts = await getProductsofProvider(Number(providerData.id));
      setActiveTab(providerProducts.length > 0 ? "tienda" : "photos");
    } catch (error) {
      console.error(error);
      setActiveTab("tienda");
    }

    // Fetch both logo and banner images concurrently
    const [logoUrl, bannerUrl] = await Promise.all([
      fetchImageSafe(providerData.logo?.imageId),
      fetchImageSafe(providerData.banner?.imageId),
    ]);

    setLogoImage(logoUrl);
    setBanner(bannerUrl);
  };

  useEffect(() => {
    if (storeName) fetchProviderData(storeName);
    //throw new Response('', { status: 500 })
  }, [storeName]);

  const breadCrumbItems: PageBreadcrumbItem[] = [
    { label: "Home", url: "/home" },
    { label: "Proveedores", url: "/home/searchpage" },
    { label: provider?.name || "Store", active: true },
  ];

  function formatJoinDate(dateString: string) {
    const formatted = dayjs(dateString).locale("es").format("MMMM YYYY");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  return (
    <div className="bg">
      <Section small className="py-0">
        <div className="d-flex align-items-center">
          <Link
            to="#"
            onClick={() => navigate(-1)}
            className="btn btn-link text-start ps-0"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
            Regresar
          </Link>
          {/* breadcrumbs */}
          <PageBreadcrumb
            items={breadCrumbItems}
            className="d-flex justify-content-start ps-0 align-items-center pt-4"
          />
        </div>
        <Row className="cards-container mb-0 p-1">
          {/* Card 1 (left) */}
          <Col className="border bg-white store-info-card position-relative">
            {/* Share button in top-right corner */}
            <button
              onClick={handleShowModal}
              className="btn position-absolute"
              style={{
                top: "15px",
                right: "15px",
                width: "25px",
                height: "25px",
                border: "none",
                borderRadius: "4px",
                padding: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
              title="Compartir perfil"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <path
                    d="M9 12C9 13.3807 7.88071 14.5 6.5 14.5C5.11929 14.5 4 13.3807 4 12C4 10.6193 5.11929 9.5 6.5 9.5C7.88071 9.5 9 10.6193 9 12Z"
                    stroke="#1C274C"
                    strokeWidth="1.5"
                  ></path>
                  <path
                    d="M14 6.5L9 10"
                    stroke="#1C274C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  ></path>
                  <path
                    d="M14 17.5L9 14"
                    stroke="#1C274C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  ></path>
                  <path
                    d="M19 18.5C19 19.8807 17.8807 21 16.5 21C15.1193 21 14 19.8807 14 18.5C14 17.1193 15.1193 16 16.5 16C17.8807 16 19 17.1193 19 18.5Z"
                    stroke="#1C274C"
                    strokeWidth="1.5"
                  ></path>
                  <path
                    d="M19 5.5C19 6.88071 17.8807 8 16.5 8C15.1193 8 14 6.88071 14 5.5C14 4.11929 15.1193 3 16.5 3C17.8807 3 19 4.11929 19 5.5Z"
                    stroke="#1C274C"
                    strokeWidth="1.5"
                  ></path>
                </g>
              </svg>
            </button>

            <div className=" d-flex mt-6 mb-3 ">
              <div className="mb-5">
                {/* Display the storeLogo image */}
                <img
                  src={logoImage ?? undefined}
                  alt={`Logo of ${provider?.name}`}
                  className="img-fluid"
                  style={{
                    maxWidth: "150px",
                    maxHeight: "150px",
                    borderRadius: "5px",
                  }}
                />
              </div>
              <div className="store-info ms-4 mt-2">
                <h3 className="">{provider?.name}</h3>
                <div className="">
                  {provider?.createdAt
                    ? `Se unio desde ${formatJoinDate(provider.createdAt)}`
                    : ""}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {provider?.tagIds &&
                    getLocations(provider.tagIds).map((location, index) => (
                      <LocationTag key={index} city={location}></LocationTag>
                    ))}
                </div>
                <div className="text-fiestr-gray d-flex flex-wrap gap-2 pt-1">
                  {provider?.tagIds &&
                    getProviderCategories(provider.tagIds).map(
                      (category, index) => (
                        <CategoryTag key={index} category={category} />
                      ),
                    )}
                </div>
                {/* verificado badge  */}
                <div style={{ display: "inline-block" }}>
                  {provider?.tagIds && isVerifiedUser(provider.tagIds) && (
                    <VerifiedBadge />
                  )}
                </div>
              </div>
            </div>
            <div
              className="grey-separator "
              style={{
                height: "2px",
                backgroundColor: "#E3E6ED",
                width: "100%",
                borderRadius: "10px",
              }}
            />
            <div className="d-flex mt-2">
              <div className="d-flex flex-column align-items-center flex-1">
                <h5 className="fs-8">Visitas al Perfil</h5>
                <div className="justify-content-center">
                  <div className="fs-7 fw-bold">{provider?.viewCount} </div>
                </div>
                <div className="text-fiestr-gray fs-9 fw-medium">
                  Últimos 30 días
                </div>
              </div>
              <div className="d-flex flex-column align-items-center flex-1 fs-8">
                <h5>Teléfono</h5>
                <p className="fs-8">{provider?.phoneNumber}</p>
              </div>
              <div className="d-flex flex-column align-items-center flex-1 fs-8">
                <h5>Domicillio</h5>
                <p className="fs-8 mb-0">{provider?.address1}</p>
              </div>
            </div>
          </Col>
          {/* Card 2 (Right)  */}
          <Col className="border bg-white p-0 store-info-card">
            <div className="d-flex">
              <img
                src={banner ?? undefined}
                alt="Banner here"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          </Col>
        </Row>
        <Row className="bg-" style={{ borderRadius: "" }}>
          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k as string)}
          >
            {/* flex-md-row-reverse so that on md+ screens, their horizontal order is reversed (tabs end up on the left, search on the right). */}
            <div
              className="d-flex flex-column flex-md-row-reverse justify-content-between align-items-center sticky-tabs-search bg-soft "
              style={{ borderRadius: "10px" }}
            >
              <div className="my-2" style={{ visibility: activeTab === "tienda" ? "visible" : "hidden" }}>
                <StoreCatalogSearch
                  value={searchTerm}
                  onChange={setSearchTerm}
                />
              </div>

              {/* Tabs second in DOM -> on BOTTOM on small screens, but flex-md-row-reverse puts them on the LEFT for md+ */}
              <Nav variant="tabs" className="mt-3 mt-md-0 store-nav-tabs">
                <Nav.Item>
                  <Nav.Link eventKey="tienda">
                    <span>
                      <FontAwesomeIcon icon={faHome} className="me-2" />
                      Catálogo
                    </span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="review">
                    <span>
                      <FontAwesomeIcon icon={faStar} className="me-2" />
                      Reseñas
                    </span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="photos">
                    <span>
                      <FontAwesomeIcon icon={faHeart} className="me-2" />
                      Galería
                    </span>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>

            <div className="sticky-scrollspy bg-soft pb-0 pt-0">
              {activeTab === "tienda" && <ScrollSpy />}
            </div>
            {/* the following will render each tab's content. the tab content will be rendered based on the activeTab state. so if activeTab is "tienda", then the EcomTienda component will be rendered. if activeTab is "review", then the EcomProfileReviewsTable component will be rendered. and so on. */}
            {/* Tab.Content is the container for all the tab panes. */}
            <Tab.Content className="">
              <Tab.Pane eventKey="tienda">
                {/* Provider's List of products*/}
                {provider && (
                  <EcomTienda
                    searchTerm={searchTerm}
                    providerId={provider.id}
                  />
                )}
              </Tab.Pane>
              <Tab.Pane eventKey="review">
                <EcomProfileReviewsTable />
              </Tab.Pane>
              <Tab.Pane eventKey="photos">
                {provider && (
                  <EcomPhotosVideo
                    providerId={provider.id}
                    isActive={activeTab === "photos"}
                  />
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Row>
      </Section>

      {/* Share Profile Modal */}
      <ShareProfileModal
        show={showModal}
        onHide={handleCloseModal}
        providerName={provider?.name || ""}
        uniqueName={storeName}
      />
    </div>
  );
};

export default Store;
// no longer using StoreTabs.tsx and migrated all the logic here so that we can have the search bar and tabs in the same row
// moved all of this logic to Store.tsx so that we can place nav tabs and serach box in the same row / container. so instead of having storetabs component return its own tab container, we will instead do everything in Store.tsx. All the tab logic (Tab.Container, Nav, Tab.Content) is in one place.The tabs and the StoreCatalogSearch share the same container:
